import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
  validatePhoneNumberLength,
} from "libphonenumber-js/max";
import type { CountryCode } from "libphonenumber-js/max";
import metadata from "libphonenumber-js/metadata.max.json";

/**
 * Phone handling for CRM forms, ported from the payment page
 * (apps/payment/src/utils/phoneValidation.ts + countryOptions.ts): a country
 * dropdown with flag, name and dial code, a digits-only number box, and
 * per-country validation from libphonenumber. Numbers are stored as one
 * international string (E.164, e.g. +918291638739) — the format the server
 * validates (RH-856).
 */

export type { CountryCode };

export const DEFAULT_COUNTRY: CountryCode = "IN";

export interface CountryOption {
  /** ISO-3166-1 alpha-2, e.g. "GB". The stable identity of the option. */
  iso: CountryCode;
  name: string;
  /** Dial code including "+", e.g. "+44". NOT unique — many countries share "+1". */
  dialCode: string;
  flag: string;
}

const regionNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

/**
 * Every country libphonenumber knows, with an English name and dial code.
 * Derived from the library rather than a hand-kept list so the dropdown can
 * never offer a code the validator rejects.
 */
export const COUNTRY_OPTIONS: CountryOption[] = getCountries()
  .map((iso) => ({
    iso,
    name: regionNames?.of(iso) ?? iso,
    dialCode: `+${getCountryCallingCode(iso)}`,
    flag: `https://flagsapi.com/${iso}/shiny/64.png`,
  }))
  .sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base" }));

const BY_ISO = new Map(COUNTRY_OPTIONS.map((o) => [o.iso, o]));

/** Every dial code, longest first, for prefix-splitting an invalid number. */
const DIAL_CODES = [...new Set(COUNTRY_OPTIONS.map((o) => o.dialCode))].sort(
  (a, b) => b.length - a.length,
);

/**
 * The main country for a dial code: "+44" is GB rather than Guernsey, "+1" is
 * US rather than American Samoa. libphonenumber lists the main country first
 * for every calling code, so this reads that rather than keeping a list here.
 */
function mainCountryForDialCode(dialCode: string): CountryCode | undefined {
  return metadata.country_calling_codes[dialCode.slice(1)]?.[0];
}

/**
 * Option to display for an ISO, falling back to a dial-code match (lossy: a
 * dial code can map to many countries), then to India. Never undefined.
 */
export function countryOptionForIso(
  iso?: string,
  dialCodeFallback?: string,
): CountryOption {
  if (iso) {
    const hit = BY_ISO.get(iso as CountryCode);
    if (hit) return hit;
  }
  if (dialCodeFallback) {
    const hit = COUNTRY_OPTIONS.find((o) => o.dialCode === dialCodeFallback);
    if (hit) return hit;
  }
  return BY_ISO.get(DEFAULT_COUNTRY) ?? COUNTRY_OPTIONS[0];
}

export interface ResolvedPhone {
  /** Dial code including "+". */
  dialCode: string;
  /** National significant number: no dial code, no trunk prefix. */
  national: string;
  /** ISO-3166-1 alpha-2, when it could be determined. */
  iso?: CountryCode;
}

function parseValid(value: string, iso?: CountryCode) {
  try {
    const parsed = iso ? parsePhoneNumber(value, iso) : parsePhoneNumber(value);
    return parsed?.isValid() ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Splits a stored number into country + national number, for any country, so
 * an edit form can preselect the flag and show only the digits.
 *
 * libphonenumber does the parsing rather than a dial-code scan: a bare
 * national number carries no country signal ("9876543210" would prefix-match
 * Iran's +98), so those are only ever read against `defaultIso`.
 */
export function resolvePhone(
  raw: string | null | undefined,
  defaultIso: CountryCode = DEFAULT_COUNTRY,
): ResolvedPhone {
  const fallbackDial = `+${getCountryCallingCode(defaultIso)}`;
  if (!raw || !/\d/.test(raw)) {
    return { dialCode: fallbackDial, national: "", iso: defaultIso };
  }

  const compact = raw.trim().replace(/[^\d+]/g, "");
  // Only "+" and the "00" international prefix declare a country code.
  let declared: string | null = null;
  if (compact.startsWith("+")) declared = compact;
  else if (compact.startsWith("00")) declared = `+${compact.slice(2)}`;

  const asResolved = (
    parsed: NonNullable<ReturnType<typeof parseValid>>,
  ): ResolvedPhone => ({
    dialCode: `+${parsed.countryCallingCode}`,
    national: parsed.nationalNumber,
    iso: parsed.country,
  });

  if (declared) {
    // 1. An explicit country code is authoritative.
    const parsed = parseValid(declared);
    if (parsed) return asResolved(parsed);
  } else {
    // 2. No declaration: read it as a national number for defaultIso first.
    const national = parseValid(compact, defaultIso);
    if (national) return asResolved(national);
    // 3. Maybe an international number that lost its "+". Accept only if that
    //    reading is genuinely valid.
    const promoted = parseValid(`+${compact}`);
    if (promoted) return asResolved(promoted);
  }

  // 4. Declared but invalid (a typo): split on the dial code so both fields
  //    still show something the user can correct in place. The dial code is
  //    mapped to its main country so the form shows the right flag, not the
  //    default one, beside digits the user still has to fix.
  if (declared) {
    const hit = DIAL_CODES.find(
      (dc) => declared!.startsWith(dc) && declared!.length > dc.length,
    );
    if (hit) {
      return {
        dialCode: hit,
        national: declared.slice(hit.length),
        iso: mainCountryForDialCode(hit),
      };
    }
  }

  // 5. No usable country signal at all.
  return {
    dialCode: fallbackDial,
    national: compact.replace(/^\+/, ""),
    iso: defaultIso,
  };
}

/**
 * Human-readable reason a number is invalid for the chosen country, or null
 * when it is fine. Empty input is the caller's decision (optional fields), so
 * it returns null here.
 *
 * Unlike the payment page, no mobile-only check: professionals may list a
 * landline.
 */
export function getPhoneValidationMessage(
  national: string,
  iso: CountryCode,
): string | null {
  const digits = national.trim();
  if (digits === "") return null;
  if (!/^\d+$/.test(digits)) {
    return "Enter digits only — the country code is picked on the left.";
  }

  const dialCode = `+${getCountryCallingCode(iso)}`;
  const dialDigits = dialCode.slice(1);
  const fullNumber = dialCode + digits;

  // A leading zero is usually a trunk prefix the user shouldn't have typed,
  // except where libphonenumber keeps it as part of the number (Italy, a few
  // African countries): compare against the parsed national number.
  if (digits.startsWith("0")) {
    let parsedNational: string | null = null;
    try {
      parsedNational = parsePhoneNumber(fullNumber, iso).nationalNumber;
    } catch {
      parsedNational = null;
    }
    if (parsedNational !== digits) {
      return "Don't start with 0 — the country code is already selected.";
    }
  }

  // Country code typed twice, e.g. 91xxxxxxxxxx with +91 selected.
  if (
    digits.startsWith(dialDigits) &&
    digits.length > dialDigits.length + 3 &&
    isValidPhoneNumber(dialCode + digits.slice(dialDigits.length), iso)
  ) {
    return `Don't include the country code (${dialCode}) — it's already selected.`;
  }

  // Name length problems precisely; the generic validity check below would
  // only say "not valid".
  const lengthProblem = validatePhoneNumberLength(fullNumber, iso);
  if (lengthProblem === "TOO_SHORT") return "Phone number is too short.";
  if (lengthProblem === "TOO_LONG") return "Phone number is too long.";

  try {
    if (!parsePhoneNumber(fullNumber, iso).isValid()) {
      return "This isn't a valid number for the selected country.";
    }
  } catch {
    return "This isn't a valid number for the selected country.";
  }

  if ((dialDigits + digits).length > 15) return "Phone number is too long.";
  return null;
}

/**
 * The stored form: one international string, e.g. "+918291638739". Empty
 * input gives "" so optional fields can clear the number.
 *
 * Throws on input libphonenumber cannot parse. Callers validate with
 * getPhoneValidationMessage first, so this only fires if that step was
 * skipped; failing loudly there beats writing a hand-assembled number the
 * library rejected.
 */
export function toE164(
  national: string | null | undefined,
  iso: CountryCode,
): string {
  const digits = (national ?? "").trim();
  if (digits === "") return "";
  return parsePhoneNumber(digits, iso).number;
}
